/* eslint-disable no-case-declarations */
/**
 * @file src/package/bridge/messagener.ts
 * @author huangxueping
 * @description 用作与原生进行通信的库
 */

import {ENV} from '../env';

interface Callback {
    onSuccess: (value?: any) => void;
    onFailure: (reason?: string) => void;
}

class Messagener {
    private callbacks: {[index: string]: Callback} = {};
    private readonly platform = ENV.platform();

    constructor() {
        this.mountGlobalCallback();
    }

    /**
     * 执行原生插件方法
     *
     * @param {string} service 调用的插件名称
     * @param {string} action 插件调用的行为
     * @param {any} actionArgs 参数
     * @returns {Promise}
     */
    exec(service: string, action: string, actionArgs?: any): Promise<any> {
        return new Promise((resolve, reject) => {
            const callbackId = this.saveCallback({
                onSuccess: (...args) => {
                    resolve(...args);
                    actionArgs.callback && actionArgs.callback();
                },
                onFailure: reject,
            });
            let paramsString = '';
            let message = null;

            switch (this.platform) {
                case 'android':
                    try {
                        paramsString = JSON.stringify(actionArgs);
                    } catch (e) {
                        throw new Error(`${actionArgs}must be an object`);
                    }
                    window?.Bridge?.exec(service, action, paramsString, callbackId);
                    break;
                case 'ios':
                    message = JSON.stringify({
                        service,
                        action,
                        arguments: actionArgs,
                        callbackId,
                    });
                    window?.webkit?.messageHandlers?.Bridge?.postMessage(message);
                    break;
                case 'browser':
                    throw new Error('无法在浏览器执行 Bridge 方法');
                default:
                    throw new Error(`无法在当前环境执行 Bridge 方法: ${this.platform}`);
            }
        });
    }

    /**
     * 生成回调函数的 id，取时间戳的后8位加上2位随机数
     */
    private genCallbackId() {
        return new Date().getTime().toString().substr(5) + Math.floor(Math.random() * 100).toString();
    }

    private saveCallback(callbackObject: Callback) {
        const callbackId = this.genCallbackId();
        this.callbacks[callbackId] = callbackObject;
        return callbackId;
    }

    /**
     * 将 callback 挂载到全局上，供原生调用
     */
    private mountGlobalCallback() {
        switch (this.platform) {
            case 'android':
                const {Bridge} = window as any;
                if (Bridge) {
                    Bridge.callback = this.execLocalCallbackFunc.bind(this);
                }
                break;
            case 'ios':
                window.BDCJSBridge = {
                    callback: this.execLocalCallbackFunc.bind(this),
                };
                break;
            // 其它环境不挂载
        }
    }

    /**
     * 原生调用该方法返回结果
     *
     * @param callbackId 任务id
     * @param isSuccess 插件调用是否成功
     * @param resultString 序列化后的结果字符串
     * @param reason 调用失败的信息
     * @param keepLive 是否保持回调, default false
     */
    private execLocalCallbackFunc(
        callbackId: string,
        isSuccess: boolean,
        resultString: string,
        reason: string,
        keepLive: boolean = false
    ) {
        let live = keepLive;
        const callback = this.callbacks[callbackId];
        if (typeof callback !== 'object') {
            throw new Error(callbackId + ' does not esist');
        }
        if (isSuccess) {
            try {
                const result = JSON.parse(resultString);
                callback.onSuccess(result);
            } catch (e) {
                callback.onSuccess(resultString);
            }
        } else {
            callback.onFailure(reason);
        }

        if (this.platform === 'android') {
            try {
                const result = JSON.parse(resultString);
                if (result && result.keepCallback === true) {
                    live = true;
                }
            } catch (e) {
                live = false;
            }
        }

        if (!live) {
            delete this.callbacks[callbackId];
        }
    }
}

export default new Messagener();
